// src/pages/ChatConversation.js
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Button,
  Form,
  InputGroup,
  Spinner,
  Modal,
} from "react-bootstrap";

const LOCAL_STORAGE_KEY = "callbunker_contacts";

const normalizePhone = (phone) => {
  if (!phone) return "";
  return phone.replace(/[^\d+]/g, "").replace(/^00/, "+");
};

const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  const cleaned = phone.replace(/[^\d]/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

function ChatConversation({ user }) {
  const { phoneNumber } = useParams();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [contactName, setContactName] = useState(null);
  const [inputText, setInputText] = useState("");
  const [sending, setSending] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showDeleteConvModal, setShowDeleteConvModal] = useState(false);
  const [deletingConv, setDeletingConv] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [deletingMessage, setDeletingMessage] = useState(false);

  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const resolveContactName = () => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (saved) {
        const list = JSON.parse(saved);
        const cleanPhone = normalizePhone(phoneNumber).replace(/^\+1/, "").replace(/^\+/, "");
        const match = list.find((c) => {
          const cPhone = normalizePhone(c.phone).replace(/^\+1/, "").replace(/^\+/, "");
          return (
            cPhone &&
            cleanPhone &&
            (cPhone === cleanPhone ||
              cleanPhone.endsWith(cPhone) ||
              cPhone.endsWith(cleanPhone))
          );
        });
        if (match && match.name && match.name !== "Unknown") {
          return match.name;
        }
      }
    } catch (e) {}

    if (
      contactName &&
      contactName !== "Verified Sender" &&
      contactName !== "Auto-added" &&
      contactName !== "Unknown"
    ) {
      return contactName;
    }
    return formatPhoneNumber(phoneNumber);
  };

  const resolvedName = resolveContactName();
  const hasCustomName = resolvedName !== formatPhoneNumber(phoneNumber);

  const scrollToBottom = (behavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const fetchThread = async (isFirst = false) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/sms/thread/${encodeURIComponent(phoneNumber)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        setMessages(res.data.messages || []);
        if (res.data.contactName) setContactName(res.data.contactName);

        // Mark as read
        await axios.put(`/api/sms/mark-read/${encodeURIComponent(phoneNumber)}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (err) {
      console.error("Error fetching chat thread:", err);
    } finally {
      if (isFirst) {
        setInitialLoading(false);
        setTimeout(() => scrollToBottom("auto"), 100);
      }
    }
  };

  useEffect(() => {
    fetchThread(true);

    // Poll every 3 seconds for new messages
    pollIntervalRef.current = setInterval(() => {
      fetchThread(false);
    }, 3000);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [phoneNumber]);

  useEffect(() => {
    scrollToBottom("smooth");
  }, [messages.length]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || sending) return;

    const textToSend = inputText.trim();
    setInputText("");
    setSending(true);

    // Optimistic message append
    const tempMsg = {
      _id: `temp_${Date.now()}`,
      direction: "outbound",
      body: textToSend,
      createdAt: new Date().toISOString(),
      status: "sending",
    };
    setMessages((prev) => [...prev, tempMsg]);
    setTimeout(() => scrollToBottom("smooth"), 50);

    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        "/api/sms/send",
        { to: phoneNumber, body: textToSend },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        setMessages((prev) =>
          prev.map((m) => (m._id === tempMsg._id ? res.data.message : m))
        );
      }
    } catch (err) {
      console.error("Failed to send message:", err);
      // Mark as failed
      setMessages((prev) =>
        prev.map((m) => (m._id === tempMsg._id ? { ...m, status: "failed" } : m))
      );
    } finally {
      setSending(false);
      setTimeout(() => scrollToBottom("smooth"), 50);
    }
  };

  const handleCall = () => {
    navigate(`/make-call?number=${encodeURIComponent(phoneNumber)}`);
  };

  const handleConfirmDeleteConversation = async () => {
    setDeletingConv(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.delete(
        `/api/sms/conversations/${encodeURIComponent(phoneNumber)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setShowDeleteConvModal(false);
        navigate("/messages");
      }
    } catch (err) {
      console.error("Failed to delete conversation:", err);
      alert(err.response?.data?.message || "Failed to delete conversation. Please try again.");
    } finally {
      setDeletingConv(false);
    }
  };

  const handleConfirmDeleteMessage = async () => {
    if (!messageToDelete) return;
    setDeletingMessage(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.delete(
        `/api/sms/messages/${encodeURIComponent(messageToDelete._id)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setMessages((prev) => prev.filter((m) => m._id !== messageToDelete._id));
        setMessageToDelete(null);
      }
    } catch (err) {
      console.error("Failed to delete message:", err);
      alert(err.response?.data?.message || "Failed to delete message. Please try again.");
    } finally {
      setDeletingMessage(false);
    }
  };



  return (
    <div
      className="d-flex flex-column"
      style={{
        height: "100vh",
        backgroundColor: "#121212",
        paddingBottom: "65px",
        paddingTop: "56px",
      }}
    >
      {/* Top Chat Bar */}
      <div
        className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom border-secondary bg-dark text-light fixed-top"
        style={{ zIndex: 1020, height: "56px" }}
      >
        <div className="d-flex align-items-center gap-2 overflow-hidden">
          <Button
            variant="link"
            className="text-light p-0 me-1"
            onClick={() => navigate("/messages")}
            style={{ textDecoration: "none" }}
          >
            <i className="fas fa-arrow-left fa-lg"></i>
          </Button>

          <div
            className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
            style={{ width: "36px", height: "36px", fontSize: "0.85rem" }}
          >
            {hasCustomName
              ? resolvedName.slice(0, 2).toUpperCase()
              : "#"}
          </div>

          <div className="overflow-hidden">
            <h6 className="mb-0 text-white text-truncate" style={{ fontSize: "0.95rem" }}>
              {resolvedName}
            </h6>
            <small className="text-muted text-truncate d-block" style={{ fontSize: "0.75rem" }}>
              {formatPhoneNumber(phoneNumber)}
            </small>
          </div>
        </div>

        {/* Actions: Call and Delete Conversation */}
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-success"
            size="sm"
            className="d-flex align-items-center gap-1 rounded-pill px-3"
            onClick={handleCall}
          >
            <i className="fas fa-phone-alt"></i>
            <span>Call</span>
          </Button>

          <Button
            variant="outline-danger"
            size="sm"
            className="d-flex align-items-center justify-content-center rounded-pill"
            style={{ width: "32px", height: "32px", padding: 0 }}
            title="Delete Conversation"
            onClick={() => setShowDeleteConvModal(true)}
          >
            <i className="fas fa-trash-alt" style={{ fontSize: "0.85rem" }}></i>
          </Button>
        </div>
      </div>

      {/* Messages Stream */}
      <div
        className="flex-grow-1 overflow-auto p-3 d-flex flex-column gap-2"
        style={{ marginTop: "10px" }}
      >
        {initialLoading ? (
          <div className="text-center py-5 m-auto">
            <Spinner animation="border" variant="success" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-5 m-auto text-muted">
            <i className="fas fa-comment-dots fa-3x mb-2 text-secondary"></i>
            <p className="mb-0">No messages yet in this conversation.</p>
            <small>Send a message below to start chatting.</small>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isOutbound = msg.direction === "outbound";
            const timeStr = msg.createdAt
              ? new Date(msg.createdAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "";

            return (
              <div
                key={msg._id || idx}
                className={`d-flex flex-column ${
                  isOutbound ? "align-items-end" : "align-items-start"
                }`}
              >
                <div
                  className="rounded-3 px-3 py-2 shadow-sm text-break"
                  style={{
                    maxWidth: "75%",
                    backgroundColor: isOutbound ? "#198754" : "#2c3034",
                    color: "#ffffff",
                    fontSize: "0.92rem",
                  }}
                >
                  {msg.body}
                </div>
                <div className="d-flex align-items-center gap-2 mt-1 px-1">
                  <span className="text-muted" style={{ fontSize: "0.68rem" }}>
                    {timeStr}
                  </span>
                  {isOutbound && (
                    <span style={{ fontSize: "0.68rem" }}>
                      {msg.status === "sending" ? (
                        <i className="fas fa-clock text-muted"></i>
                      ) : msg.status === "failed" ? (
                        <i className="fas fa-exclamation-circle text-danger"></i>
                      ) : (
                        <i className="fas fa-check text-muted"></i>
                      )}
                    </span>
                  )}
                  {msg._id && !String(msg._id).startsWith("temp_") && (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-secondary"
                      style={{
                        fontSize: "0.72rem",
                        textDecoration: "none",
                        lineHeight: 1,
                        opacity: 0.6,
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "#ef4444";
                        e.currentTarget.style.opacity = "1";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "#6c757d";
                        e.currentTarget.style.opacity = "0.6";
                      }}
                      title="Delete this message"
                      onClick={() => setMessageToDelete(msg)}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input Bottom Bar */}
      <div
        className="p-2 border-top border-secondary bg-dark"
        style={{ position: "relative", zIndex: 1010 }}
      >
        <Form onSubmit={handleSendMessage}>
          <InputGroup>
            <Form.Control
              type="text"
              className="bg-secondary bg-opacity-25 text-light border-secondary"
              placeholder="Text message..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              disabled={sending}
              autoFocus
            />
            <Button
              variant="success"
              type="submit"
              disabled={!inputText.trim() || sending}
              className="d-flex align-items-center justify-content-center px-3"
            >
              {sending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <i className="fas fa-paper-plane"></i>
              )}
            </Button>
          </InputGroup>
        </Form>
      </div>

      {/* Modal: Confirm Delete Entire Conversation */}
      <Modal
        show={showDeleteConvModal}
        onHide={() => !deletingConv && setShowDeleteConvModal(false)}
        centered
        contentClassName="border-0 shadow-lg text-light"
      >
        <div style={{ backgroundColor: "#1e2433", borderRadius: "14px", overflow: "hidden" }}>
          <Modal.Header
            closeButton={!deletingConv}
            closeVariant="white"
            style={{ borderBottom: "1px solid #334155", padding: "16px 20px" }}
          >
            <Modal.Title className="fs-6 fw-bold text-white d-flex align-items-center gap-2">
              <i className="fas fa-trash-alt text-danger"></i>
              <span>Delete Conversation</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4" style={{ color: "#f8fafc" }}>
            <p className="mb-2">
              Are you sure you want to delete your conversation with{" "}
              <strong className="text-white">{resolvedName}</strong>?
            </p>
            <p className="text-secondary small mb-0">
              This will permanently delete all incoming and outgoing messages in this thread. This action cannot be undone.
            </p>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: "1px solid #334155", padding: "14px 20px" }}>
            <Button
              variant="outline-secondary"
              className="text-light px-3"
              size="sm"
              disabled={deletingConv}
              onClick={() => setShowDeleteConvModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              className="px-4 fw-semibold shadow-sm d-flex align-items-center gap-2"
              disabled={deletingConv}
              onClick={handleConfirmDeleteConversation}
            >
              {deletingConv ? (
                <>
                  <Spinner animation="border" size="sm" />
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-trash-alt"></i>
                  <span>Delete Conversation</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      {/* Modal: Confirm Delete Single Message */}
      <Modal
        show={!!messageToDelete}
        onHide={() => !deletingMessage && setMessageToDelete(null)}
        centered
        contentClassName="border-0 shadow-lg text-light"
      >
        <div style={{ backgroundColor: "#1e2433", borderRadius: "14px", overflow: "hidden" }}>
          <Modal.Header
            closeButton={!deletingMessage}
            closeVariant="white"
            style={{ borderBottom: "1px solid #334155", padding: "16px 20px" }}
          >
            <Modal.Title className="fs-6 fw-bold text-white d-flex align-items-center gap-2">
              <i className="fas fa-trash-alt text-danger"></i>
              <span>Delete Message</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4" style={{ color: "#f8fafc" }}>
            <p className="mb-2">Are you sure you want to delete this message?</p>
            <div
              className="p-2 rounded text-light small mb-2 text-break"
              style={{ backgroundColor: "#0f172a", border: "1px solid #334155", fontStyle: "italic" }}
            >
              "{messageToDelete?.body}"
            </div>
            <p className="text-secondary small mb-0">
              This message will be permanently removed from your chat history.
            </p>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: "1px solid #334155", padding: "14px 20px" }}>
            <Button
              variant="outline-secondary"
              className="text-light px-3"
              size="sm"
              disabled={deletingMessage}
              onClick={() => setMessageToDelete(null)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              className="px-4 fw-semibold shadow-sm d-flex align-items-center gap-2"
              disabled={deletingMessage}
              onClick={handleConfirmDeleteMessage}
            >
              {deletingMessage ? (
                <>
                  <Spinner animation="border" size="sm" />
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-trash-alt"></i>
                  <span>Delete Message</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
}

export default ChatConversation;
